/**
 * Lightweight signature management for Settings → Profile (ENG-3443).
 * Uses the same Basic-backed store as SignatureCaptureModal (`useUserSignature`).
 */
import { useMemo, useState } from 'react';
import { Button, Group, Paper, Stack, Text, Title } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconEdit, IconTrash } from '@tabler/icons-react';
import {
  isDrawnSignature,
  legalNameForSignature,
  SignatureCaptureModal,
  SignaturePreviewPanel,
} from '../../../components/SignatureCapture';
import { useUserSignature } from '../../../hooks/useUserSignature';
import { useCurrentUserPractitioner } from '../../../hooks/useCurrentUserPractitioner';
import { formatPractitionerLicenseCredential } from '../../../lib/practitionerLicenseCredential';

export function ProfileSignatureSettings(): JSX.Element {
  const { config, loading, saveConfig } = useUserSignature();
  const { practitioner } = useCurrentUserPractitioner();
  const [captureOpen, setCaptureOpen] = useState(false);
  const [clearing, setClearing] = useState(false);
  const saved = config.savedSignature;
  const hasSignature = Boolean(saved?.signatureData);
  const legalName = useMemo(
    () => legalNameForSignature(practitioner ?? undefined, saved?.name ?? ''),
    [practitioner, saved?.name],
  );
  const licenseLine = useMemo(
    () => formatPractitionerLicenseCredential(practitioner ?? undefined) ?? saved?.license,
    [practitioner, saved?.license],
  );
  const drawingSrc = isDrawnSignature(saved, config.preferredMode) && saved?.signatureData
    ? `data:${saved.signatureFormat ?? 'image/png'};base64,${saved.signatureData}`
    : undefined;

  const handleClear = async () => {
    setClearing(true);
    try {
      await saveConfig({ preferredMode: config.preferredMode, savedSignature: undefined });
      notifications.show({
        title: 'Signature cleared',
        message: 'Your saved signature was removed.',
        color: 'green',
      });
    } catch {
      notifications.show({
        title: 'Could not clear signature',
        message: 'Please try again.',
        color: 'red',
      });
    } finally {
      setClearing(false);
    }
  };

  return (
    <>
      <Paper p={32} withBorder radius="md" shadow="sm" mt="lg">
        <Stack gap="md">
          <div>
            <Title order={4}>Signature</Title>
            <Text size="sm" c="dimmed">
              Saved signature used when signing clinical notes. Update it here or when signing a
              document.
            </Text>
          </div>
          {loading ? (
            <Text size="sm" c="dimmed">
              Loading signature…
            </Text>
          ) : (
            <SignaturePreviewPanel
              name={legalName}
              license={licenseLine}
              signedAt={saved?.signedAt}
              drawingSrc={drawingSrc}
            />
          )}
          <Group gap="sm">
            <Button
              leftSection={<IconEdit size={16} />}
              color="gold"
              onClick={() => setCaptureOpen(true)}
            >
              {hasSignature ? 'Update signature' : 'Add signature'}
            </Button>
            {hasSignature ? (
              <Button
                variant="light"
                color="red"
                leftSection={<IconTrash size={16} />}
                loading={clearing}
                onClick={() => void handleClear()}
              >
                Clear
              </Button>
            ) : null}
          </Group>
        </Stack>
      </Paper>

      <SignatureCaptureModal
        opened={captureOpen}
        onClose={() => setCaptureOpen(false)}
        allowSaveForFuture
        title="Update signature"
        submitLabel="Save signature"
        onSign={(signature) => {
          void (async () => {
            try {
              await saveConfig({ preferredMode: config.preferredMode, savedSignature: signature });
              setCaptureOpen(false);
              notifications.show({
                title: 'Signature saved',
                message: 'Your signature will be available when signing notes.',
                color: 'green',
              });
            } catch {
              notifications.show({
                title: 'Could not save signature',
                message: 'Please try again.',
                color: 'red',
              });
            }
          })();
        }}
      />
    </>
  );
}
